import { Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  UntypedFormBuilder,
  UntypedFormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UserService } from '../../../user-admin/services/user.service';

export type Password = {
  password: string;
  confirmPassword: string;
};

@Component({
  selector: 'mpro-password',
  templateUrl: './password.component.html',
  styleUrls: ['./password.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PasswordComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PasswordComponent),
      multi: true,
    },
  ],
})
export class PasswordComponent
  implements OnInit, OnDestroy, ControlValueAccessor, Validator
{
  public passwordForm: UntypedFormGroup;
  @Input() public labels: boolean | undefined = undefined;
  @Input() public passwordPlaceholder: string = 'Type the password';
  @Input() public confirmPasswordPlaceholder: string = 'Confirm the password';

  private onChange = (_: any) => {};
  private onTouched = (_: any) => {};
  private ngUnsubscribe: Subject<void> = new Subject();
  public passwordStrength: number = 0;

  constructor(private fb: UntypedFormBuilder, private userService: UserService) {}

  public writeValue(val: Password): void {
    if (val) {
      this.passwordForm.setValue(val);
    }
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public validate(control: AbstractControl): ValidationErrors | null {
    return !this.passwordForm.valid || this.getPasswordMatchMessage()
      ? {
          invalidValue: {
            message: 'The control is invalid. Check the entered values.',
          },
        }
      : null;
  }

  public onPasswordStrengthChange(strength: number): void {
    this.passwordStrength = strength;
  }

  public ngOnInit(): void {
    this.passwordForm = this.fb.group({
      password: ['', [Validators.required, this.userService.passwordValidator]],
      passwordConfirm: ['', Validators.required],
    });

    this.passwordForm.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((_) => this.onChange(this.passwordForm.value));
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public getClass(
    errorType: 'hasUpper' | 'hasLower' | 'hasNumber' | 'hasLength'
  ): string {
    const password: AbstractControl = this.passwordForm.controls.password;
    if (!password.value) {
      return '';
    }

    if (!password.errors) {
      return 'valid';
    }

    return password.errors[errorType] ? 'valid' : 'invalid';
  }

  public getPasswordMatchMessage(): string | undefined {
    if (
      !this.passwordForm.controls.password.value ||
      !this.passwordForm.controls.passwordConfirm.value
    ) {
      return undefined;
    }

    const areEqual =
      this.passwordForm.controls.password.value ===
      this.passwordForm.controls.passwordConfirm.value;

    return areEqual ? undefined : `The passwords don't match`;
  }
}
